import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

export type TableSize = 'sm' | 'md' | 'lg';

export interface TablePreferenceDefaults {
  striped: boolean;
  size: TableSize;
  bordered: boolean;
  showRowNumbers: boolean;
  pageSize: number;
  stickyHeader: boolean;
  stickyFirstColumn: boolean;
  scrollHeight: number | null;
  showSearch: boolean;
  showColumnToggle: boolean;
}

interface TablePreferences extends TablePreferenceDefaults {
  setStriped: (value: boolean) => void;
  setSize: (value: TableSize) => void;
  setBordered: (value: boolean) => void;
  setShowRowNumbers: (value: boolean) => void;
  setPageSize: (value: number) => void;
  setStickyHeader: (value: boolean) => void;
  setStickyFirstColumn: (value: boolean) => void;
  setScrollHeight: (value: number | null) => void;
  setShowSearch: (value: boolean) => void;
  setShowColumnToggle: (value: boolean) => void;
}

const TABLE_DEFAULTS: TablePreferenceDefaults = {
  striped: true,
  size: 'sm',
  bordered: false,
  showRowNumbers: false,
  pageSize: 10,
  stickyHeader: true,
  stickyFirstColumn: false,
  scrollHeight: null,
  showSearch: false,
  showColumnToggle: false,
};

const TablePreferencesContext = createContext<TablePreferences>({
  ...TABLE_DEFAULTS,
  setStriped: () => {},
  setSize: () => {},
  setBordered: () => {},
  setShowRowNumbers: () => {},
  setPageSize: () => {},
  setStickyHeader: () => {},
  setStickyFirstColumn: () => {},
  setScrollHeight: () => {},
  setShowSearch: () => {},
  setShowColumnToggle: () => {},
});

const STORAGE_KEY = 'table-preferences';

function load(): TablePreferenceDefaults {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...TABLE_DEFAULTS, ...(JSON.parse(raw) as Partial<TablePreferenceDefaults>) };
  } catch {
    // ignore
  }
  return TABLE_DEFAULTS;
}

function save(prefs: TablePreferenceDefaults) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // ignore
  }
}

export function useTablePreferences() {
  return useContext(TablePreferencesContext);
}

export function TablePreferencesProvider({ children }: { children: React.ReactNode }) {
  const [striped, setStripedState] = useState(TABLE_DEFAULTS.striped);
  const [size, setSizeState] = useState<TableSize>(TABLE_DEFAULTS.size);
  const [bordered, setBorderedState] = useState(TABLE_DEFAULTS.bordered);
  const [showRowNumbers, setShowRowNumbersState] = useState(TABLE_DEFAULTS.showRowNumbers);
  const [pageSize, setPageSizeState] = useState(TABLE_DEFAULTS.pageSize);
  const [stickyHeader, setStickyHeaderState] = useState(TABLE_DEFAULTS.stickyHeader);
  const [stickyFirstColumn, setStickyFirstColumnState] = useState(TABLE_DEFAULTS.stickyFirstColumn);
  const [scrollHeight, setScrollHeightState] = useState<number | null>(TABLE_DEFAULTS.scrollHeight);
  const [showSearch, setShowSearchState] = useState(TABLE_DEFAULTS.showSearch);
  const [showColumnToggle, setShowColumnToggleState] = useState(TABLE_DEFAULTS.showColumnToggle);
  const current = useRef<TablePreferenceDefaults>(TABLE_DEFAULTS);

  useEffect(() => {
    const stored = load();
    current.current = stored;
    setStripedState(stored.striped);
    setSizeState(stored.size);
    setBorderedState(stored.bordered);
    setShowRowNumbersState(stored.showRowNumbers);
    setPageSizeState(stored.pageSize);
    setStickyHeaderState(stored.stickyHeader);
    setStickyFirstColumnState(stored.stickyFirstColumn);
    setScrollHeightState(stored.scrollHeight);
    setShowSearchState(stored.showSearch);
    setShowColumnToggleState(stored.showColumnToggle);
  }, []);

  const setStriped = useCallback((value: boolean) => {
    current.current = { ...current.current, striped: value };
    save(current.current);
    setStripedState(value);
  }, []);

  const setSize = useCallback((value: TableSize) => {
    current.current = { ...current.current, size: value };
    save(current.current);
    setSizeState(value);
  }, []);

  const setBordered = useCallback((value: boolean) => {
    current.current = { ...current.current, bordered: value };
    save(current.current);
    setBorderedState(value);
  }, []);

  const setShowRowNumbers = useCallback((value: boolean) => {
    current.current = { ...current.current, showRowNumbers: value };
    save(current.current);
    setShowRowNumbersState(value);
  }, []);

  const setPageSize = useCallback((value: number) => {
    current.current = { ...current.current, pageSize: value };
    save(current.current);
    setPageSizeState(value);
  }, []);

  const setStickyHeader = useCallback((value: boolean) => {
    current.current = { ...current.current, stickyHeader: value };
    save(current.current);
    setStickyHeaderState(value);
  }, []);

  const setStickyFirstColumn = useCallback((value: boolean) => {
    current.current = { ...current.current, stickyFirstColumn: value };
    save(current.current);
    setStickyFirstColumnState(value);
  }, []);

  const setScrollHeight = useCallback((value: number | null) => {
    current.current = { ...current.current, scrollHeight: value };
    save(current.current);
    setScrollHeightState(value);
  }, []);

  const setShowSearch = useCallback((value: boolean) => {
    current.current = { ...current.current, showSearch: value };
    save(current.current);
    setShowSearchState(value);
  }, []);

  const setShowColumnToggle = useCallback((value: boolean) => {
    current.current = { ...current.current, showColumnToggle: value };
    save(current.current);
    setShowColumnToggleState(value);
  }, []);

  return (
    <TablePreferencesContext.Provider
      value={{
        striped,
        size,
        bordered,
        showRowNumbers,
        pageSize,
        stickyHeader,
        stickyFirstColumn,
        scrollHeight,
        showSearch,
        showColumnToggle,
        setStriped,
        setSize,
        setBordered,
        setShowRowNumbers,
        setPageSize,
        setStickyHeader,
        setStickyFirstColumn,
        setScrollHeight,
        setShowSearch,
        setShowColumnToggle,
      }}
    >
      {children}
    </TablePreferencesContext.Provider>
  );
}
